import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { getUniversalDescriptors } from "@/api/universal-descriptors-api.js";
import { errorUtils } from "@/lib/utils.js";
import { useDataLoaderContext } from "./DataLoaderContext.jsx";

/**
 * @typedef {Object} UniversalDescriptor
 * @property {number} UniversalDescriptorId
 * @property {string} UD
 * @property {string} FriendlyName
 * @property {boolean} IsActive
 */

/**
 * @typedef {Object} UniversalDescriptorsContextType
 * @property {UniversalDescriptor[]} universalDescriptors - Cached list from the API
 * @property {boolean} isLoading - True while a fetch is in flight
 * @property {string|null} error - User-facing error message, or null when none
 */

const UniversalDescriptorsContext = createContext(
  /** @type {UniversalDescriptorsContextType|null} */ (null)
);

/**
 * @returns {UniversalDescriptorsContextType}
 */
export const useUniversalDescriptorsContext = () => {
  const context = useContext(UniversalDescriptorsContext);
  if (!context) {
    throw new Error(
      "useUniversalDescriptorsContext must be used within a UniversalDescriptorsProvider"
    );
  }
  return context;
};

/**
 * Loads universal descriptors on the shared app refresh interval (see {@link DataLoaderProvider}).
 *
 * @param {{ children: React.ReactNode }} props - Provider props
 * @returns {JSX.Element}
 */
export function UniversalDescriptorsProvider({ children }) {
  const { triggerCount } = useDataLoaderContext();

  const [universalDescriptors, setUniversalDescriptors] = useState(
    /** @type {UniversalDescriptor[]} */ ([])
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(/** @type {string|null} */ (null));

  const fetchUniversalDescriptors = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getUniversalDescriptors();
      setUniversalDescriptors(Array.isArray(data) ? data : []);
    } catch (err) {
      errorUtils.handleApiError("fetching universal descriptors", err);
      setUniversalDescriptors([]);
      setError("Failed to load universal descriptors");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (triggerCount > 0) {
      fetchUniversalDescriptors();
    }
  }, [triggerCount, fetchUniversalDescriptors]);

  const value = useMemo(
    () => ({
      universalDescriptors,
      isLoading,
      error,
    }),
    [universalDescriptors, isLoading, error]
  );

  return (
    <UniversalDescriptorsContext.Provider value={value}>
      {children}
    </UniversalDescriptorsContext.Provider>
  );
}
